/**
 * Per-device List view state (Boards Redesign cards 6.1 + 6.3, 12.10.2026):
 * which sections of a board are collapsed, and the list's sort order.
 *
 * Local and never synced, like the overview prefs (boardsLocalPrefs.js):
 * collapsing a section on a laptop must not fold it on a teammate's screen,
 * and none of it may enter a board record. Stored as one map keyed by board
 * id, capped so boards that were deleted long ago do not pile up.
 *
 * Every access is guarded — a private window, blocked storage or a corrupt
 * value falls back to "all open, manual order".
 */
import { normalizeListSort, SORT_MANUAL } from './listViewSort';

export const LIST_VIEW_PREFS_KEY = 'apiboo.boards.listViewPrefs';
/** Boards kept in the map; the least recently touched beyond this are dropped. */
export const LIST_VIEW_PREFS_MAX = 200;

export const DEFAULT_BOARD_LIST_PREFS = Object.freeze({ sort: SORT_MANUAL, collapsed: Object.freeze([]), at: 0 });

function clean(entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const collapsed = Array.isArray(entry.collapsed) ? [...new Set(entry.collapsed.filter((id) => typeof id === 'string' && id))] : [];
  const at = typeof entry.at === 'number' && Number.isFinite(entry.at) ? entry.at : 0;
  return { sort: normalizeListSort(entry.sort), collapsed, at };
}

/** Parse whatever is stored into `{ [boardId]: { sort, collapsed, at } }`. */
export function parseListViewPrefs(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [id, entry] of Object.entries(raw)) {
    const c = clean(entry);
    if (c) out[id] = c;
  }
  return out;
}

export function loadListViewPrefs() {
  try {
    const raw = window.localStorage.getItem(LIST_VIEW_PREFS_KEY);
    return parseListViewPrefs(raw ? JSON.parse(raw) : null);
  } catch {
    return {};
  }
}

export function saveListViewPrefs(prefs) {
  try {
    window.localStorage.setItem(LIST_VIEW_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage full or blocked: the in-memory value still applies this session.
  }
}

/** The prefs of one board (defaults when it has none). */
export function boardListPrefs(prefs, boardId) {
  return (boardId && prefs?.[boardId]) || DEFAULT_BOARD_LIST_PREFS;
}

function capped(map) {
  const ids = Object.keys(map);
  if (ids.length <= LIST_VIEW_PREFS_MAX) return map;
  const keep = ids.sort((a, b) => (map[b].at || 0) - (map[a].at || 0)).slice(0, LIST_VIEW_PREFS_MAX);
  return Object.fromEntries(keep.map((id) => [id, map[id]]));
}

function withEntry(prefs, boardId, patch, at) {
  if (!boardId) return prefs || {};
  const prev = boardListPrefs(prefs, boardId);
  return capped({ ...(prefs || {}), [boardId]: { ...prev, ...patch, at } });
}

/** `prefs` with `groupId` of `boardId` collapsed (true) or open (false). */
export function withCollapsed(prefs, boardId, groupId, collapsed, at = Date.now()) {
  const prev = boardListPrefs(prefs, boardId).collapsed;
  const has = prev.includes(groupId);
  if (!groupId || has === !!collapsed) return prefs || {};
  const next = collapsed ? [...prev, groupId] : prev.filter((id) => id !== groupId);
  return withEntry(prefs, boardId, { collapsed: next }, at);
}

/** `prefs` with the list sort of `boardId` set. */
export function withSort(prefs, boardId, sort, at = Date.now()) {
  return withEntry(prefs, boardId, { sort: normalizeListSort(sort) }, at);
}
